/**
 * Frontend payload-crypto: ECDH P-256 + HKDF-SHA256 + AES-256-GCM via WebCrypto.
 * Gated by VITE_ENABLE_PAYLOAD_CRYPTO. If the flag is off the helpers no-op.
 */

const ENABLED = import.meta.env.VITE_ENABLE_PAYLOAD_CRYPTO === '1';

let _aesKey: CryptoKey | null = null;
let _keyExchangePromise: Promise<void> | null = null;
let _pendingKeyPair: CryptoKeyPair | null = null;
let _pendingPubB64: string | null = null;

export function isPayloadCryptoEnabled(): boolean { return ENABLED; }
export function isPayloadCryptoReady(): boolean { return ENABLED && _aesKey !== null; }

export function resetPayloadCrypto(): void {
  _aesKey = null;
  _keyExchangePromise = null;
  _pendingKeyPair = null;
  _pendingPubB64 = null;
}

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Pre-generate an ECDH P-256 keypair so the public key can be piggy-backed
 * onto another request (e.g. /auth/login). Returns the base64 public key.
 */
export async function prepareClientKeyExchange(): Promise<string | null> {
  if (!ENABLED) return null;
  const kp = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  );
  const rawPub = await crypto.subtle.exportKey('raw', kp.publicKey);
  _pendingKeyPair = kp;
  _pendingPubB64 = b64encode(rawPub);
  return _pendingPubB64;
}

/**
 * Complete the key exchange using a serverPublicKey returned alongside a
 * piggy-backed response (e.g. /auth/login). Must be preceded by
 * prepareClientKeyExchange().
 */
export async function completeClientKeyExchange(serverPublicKeyB64: string, sessionId: string): Promise<void> {
  if (!ENABLED || !_pendingKeyPair) return;
  const kp = _pendingKeyPair;
  _pendingKeyPair = null;
  _pendingPubB64 = null;
  const serverPubKey = await crypto.subtle.importKey(
    'raw',
    b64decode(serverPublicKeyB64).buffer as ArrayBuffer,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: serverPubKey },
    kp.privateKey,
    256,
  );
  const hkdfKey = await crypto.subtle.importKey('raw', sharedBits as ArrayBuffer, 'HKDF', false, ['deriveKey']);
  _aesKey = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(sessionId).buffer as ArrayBuffer,
      info: new TextEncoder().encode('febix-e2e-v1').buffer as ArrayBuffer,
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Exchange keys with the gateway. Idempotent — safe to call after every login.
 * fetcher receives ('/auth/key-exchange', body) and must return the server's
 * JSON response { serverPublicKey: string } authenticated with the user's
 * Bearer token.
 */
export async function initPayloadCrypto(
  sessionId: string,
  fetcher: (path: string, body: { clientPublicKey: string }) => Promise<{ serverPublicKey: string }>,
): Promise<void> {
  if (!ENABLED) return;
  if (_aesKey) return;
  if (_keyExchangePromise) return _keyExchangePromise;

  _keyExchangePromise = (async () => {
    const kp = await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveBits'],
    );
    const rawPub = await crypto.subtle.exportKey('raw', kp.publicKey);
    const { serverPublicKey } = await fetcher('/auth/key-exchange', {
      clientPublicKey: b64encode(rawPub),
    });
    const serverPubKey = await crypto.subtle.importKey(
      'raw',
      b64decode(serverPublicKey).buffer as ArrayBuffer,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
    const sharedBits = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: serverPubKey },
      kp.privateKey,
      256,
    );
    // HKDF-SHA256 with salt = sessionId, info = "febix-e2e-v1" → 32-byte AES key
    const hkdfKey = await crypto.subtle.importKey(
      'raw',
      sharedBits as ArrayBuffer,
      'HKDF',
      false,
      ['deriveKey'],
    );
    _aesKey = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new TextEncoder().encode(sessionId).buffer as ArrayBuffer,
        info: new TextEncoder().encode('febix-e2e-v1').buffer as ArrayBuffer,
      },
      hkdfKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  })().catch((err) => {
    if (import.meta.env.DEV) console.warn('payload-crypto init failed', err);
    _aesKey = null;
    _keyExchangePromise = null;
    throw err;
  });

  return _keyExchangePromise;
}

export interface Envelope { iv: string; ct: string }

export async function encryptJson(obj: unknown): Promise<Envelope | null> {
  if (!_aesKey) return null;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plain = new TextEncoder().encode(JSON.stringify(obj));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    _aesKey,
    plain.buffer as ArrayBuffer,
  );
  return { iv: b64encode(iv), ct: b64encode(ct) };
}

export async function decryptJson(env: Envelope): Promise<unknown> {
  if (!_aesKey) throw new Error('payload-crypto-not-initialized');
  const iv = b64decode(env.iv).buffer as ArrayBuffer;
  const ct = b64decode(env.ct).buffer as ArrayBuffer;
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, _aesKey, ct);
  return JSON.parse(new TextDecoder().decode(plain));
}